import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Globe, Plus, DollarSign, Clock, Activity } from 'react-feather';
import axios from 'axios';

import UploadsCountChart from './Charts/UploadsCountChart.component';
import UploadedFilesCard from './Cards/UploadedFilesCard.component';
import GettingStartedCard from './Cards/GettingStartedCard.component';

import useOrgIdentifer from '@hooks/org_identifier';

const StatCard = ({ icon, label, value, accent = '#0d6efd', sub }) => (
  <Card className='shadow-sm border-0 h-100'>
    <CardBody className='d-flex align-items-start' style={{ padding: '18px 20px' }}>
      <div
        className='d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0'
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: `${accent}15`,
          color: accent,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          className='text-muted'
          style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          {label}
        </div>
        <div
          className='text-dark text-truncate'
          style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2, marginTop: 4 }}
        >
          {value ?? '—'}
        </div>
        {sub && (
          <div className='text-muted' style={{ fontSize: 12, marginTop: 2 }}>
            {sub}
          </div>
        )}
      </div>
    </CardBody>
  </Card>
);

export default function ClientHome({ current_organization, current_user, take_my_org_details }) {
  const history = useHistory();
  const org_identifier = useOrgIdentifer();

  const [data, setData] = useState({});
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartCategory, setChartCategory] = useState([]);

  useEffect(() => {
    take_my_org_details(org_identifier);
  }, [org_identifier]);

  useEffect(() => {
    if (org_identifier) {
      setLoading(true);
      axios
        .get(`/api/v1/organizations/${org_identifier}/dashboard/fetch_dashboard_details_from_es?page_type=client&controller_name=dashboard`)
        .then((res) => {
          setData(res?.data?.dashboard_details || {});
          setSeries([{ name: 'upload count', data: res?.data?.dashboard_details?.uploads_count || [] }]);
          setChartCategory(res?.data?.dashboard_details?.days || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [org_identifier]);

  const chartOptions = useMemo(
    () => ({
      chart: { height: 240, type: 'area', toolbar: { show: false } },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: {
        type: 'category',
        categories: chartCategory,
        labels: { show: true, style: { fontSize: '11px', colors: '#6c757d' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      grid: { show: false, padding: { left: 0, right: 0, bottom: -10, top: -20 } },
      colors: ['#0d6efd'],
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] },
      },
      legend: { show: false },
      tooltip: { x: { format: 'dd MMM' } },
    }),
    [chartCategory]
  );

  const userName =
    current_user?.firstname || current_user?.name || (current_user?.email || '').split('@')[0] || '';

  return (
    <Container fluid>
      {/* Welcome banner */}
      <Card className='shadow-sm border-0 mb-3' style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
        <CardBody className='text-white' style={{ padding: '24px 28px' }}>
          <h4 className='mb-1 text-white' style={{ fontWeight: 600 }}>
            {userName ? `Welcome back, ${userName}` : 'Welcome to CrawlBoard'}
          </h4>
          <p className='mb-0' style={{ opacity: 0.85, fontSize: 14 }}>
            Here’s what’s happening with your crawls today.
          </p>
        </CardBody>
      </Card>

      {/* KPI row */}
      <Row className='g-3 mb-3'>
        <Col xs={6} md={3}>
          <StatCard icon={<Globe size={20} />} label='Active Sites' value={data?.active_sites} accent='#0d6efd' />
        </Col>
        <Col xs={6} md={3}>
          <StatCard icon={<Plus size={20} />} label='New Records Today' value={data?.new_records_today} accent='#198754' />
        </Col>
        <Col xs={6} md={3}>
          <StatCard icon={<DollarSign size={20} />} label='Current Bill' value={data?.current_bill} accent='#fd7e14' />
        </Col>
        <Col xs={6} md={3}>
          <StatCard
            icon={<Clock size={20} />}
            label='Last Crawl'
            value={data?.last_crawl_date || '—'}
            accent='#6f42c1'
            sub={data?.next_crawl_date ? `Next: ${data.next_crawl_date}` : null}
          />
        </Col>
      </Row>

      {/* Upload trend chart */}
      <Card className='shadow-sm border-0 mb-3'>
        <CardBody style={{ padding: 24 }}>
          <div className='d-flex align-items-start justify-content-between mb-3'>
            <div>
              <h6 className='fw-semibold mb-1 text-dark' style={{ fontSize: 16 }}>
                <Activity size={16} className='me-2' style={{ color: '#0d6efd' }} />
                Records uploaded — last 30 days
              </h6>
              <p className='text-muted mb-0' style={{ fontSize: 13 }}>
                Daily upload activity across all sites in your account.
              </p>
            </div>
            {data?.last_upload_date && (
              <div className='text-end'>
                <div className='text-muted' style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Last upload
                </div>
                <div className='fw-semibold text-dark' style={{ fontSize: 13 }}>{data.last_upload_date}</div>
              </div>
            )}
          </div>
          <UploadsCountChart chartOptions={chartOptions} chartSeries={series} loading={loading} />
        </CardBody>
      </Card>

      {/* Files (8) + Getting Started (4) */}
      <Row className='g-3'>
        <Col lg={8}>
          <UploadedFilesCard />
        </Col>
        <Col lg={4}>
          <GettingStartedCard org_identifier={org_identifier} />
        </Col>
      </Row>
    </Container>
  );
}
